// Local-dev test helpers (see DevTools.jsx). Kept free of React/MUI so the API
// layer can import it cheaply. Every export is a no-op unless DEVTOOLS_ENABLED.

const STORAGE_KEY = "irDevtools";

export const DEVTOOLS_ENABLED =
  process.env.NODE_ENV === "development" &&
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const DEFAULT_STATE = {
  levelOverride: null,
  dryRun: false,
  // { [courseId]: ["sectionId|chapterId", ...] } completed while dry-running
  localPasses: {},
  recentUsers: [],
};

// Not persisted: how many writes dry-run swallowed since the page loaded.
let suppressedWrites = 0;
const listeners = new Set();

function read() {
  try {
    return { ...DEFAULT_STATE, ...JSON.parse(localStorage.getItem(STORAGE_KEY)) };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

let state = DEVTOOLS_ENABLED ? read() : DEFAULT_STATE;

function emit() {
  listeners.forEach((fn) => fn());
}

export function getDevtoolsState() {
  return { ...state, suppressedWrites };
}

export function setDevtoolsState(patch) {
  if (!DEVTOOLS_ENABLED) return;
  state = { ...state, ...(typeof patch === "function" ? patch(state) : patch) };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  emit();
}

export function subscribeDevtools(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const isDryRun = () => DEVTOOLS_ENABLED && state.dryRun;

// The app wipes localStorage on login/logout/expiry. Use this instead so the
// devtools settings survive switching students.
export function clearLocalStorage() {
  localStorage.clear();
  if (DEVTOOLS_ENABLED) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

// ---- dry-run write interception (called from utils/api.js) ----

// Endpoints that record progress. Reads, TTS and speech evaluation stay live.
const WRITE_BLOCKLIST = [
  /\/v1\/api\/activities/,
  /\/assignmentProgress\//,
  /\/courseAssignments/,
  /\/assessments\/answers/,
  /\/assessments\/[^/]+\/answers/,
  /\/studentAssessmentResult/,
  /\/students\/profile\/ir\//,
  /\/file\/upload/,
  /\/speech-evaluation-history/,
  /\/v1\/api\/assessments\/evaluate$/,
];

const passKey = (sectionId, chapterId) => `${sectionId}|${chapterId}`;

function recordFromBody(url, body) {
  if (!body || typeof body !== "object" || body instanceof FormData) return;

  if (body.type === "CHAPTER_COMPLETE" && body.courseId && body.chapterId) {
    const key = passKey(body.sectionId, body.chapterId);
    setDevtoolsState((s) => {
      const list = s.localPasses[body.courseId] || [];
      if (list.includes(key)) return {};
      return { localPasses: { ...s.localPasses, [body.courseId]: [...list, key] } };
    });
  }

  // Diagnostic placement: land the level locally instead of on the profile.
  if (/\/students\/profile\/ir\//.test(url) && body.gradeLevel) {
    setDevtoolsState({ levelOverride: body.gradeLevel });
  }
}

// Returns a fake response when dry-run should swallow this write, else null.
export function interceptWrite(method, url, body) {
  if (!isDryRun() || !WRITE_BLOCKLIST.some((re) => re.test(url))) return null;
  suppressedWrites += 1;
  recordFromBody(url, body);
  emit();
  console.info(`[devtools] dry-run suppressed ${method} ${url}`, body);
  return { status: 200, data: {}, message: "devtools dry-run" };
}

// ---- response patches ----

// Treat sessions completed during dry-run as passed so the in-course lock opens.
export function patchSessionGate(response, courseId) {
  if (!DEVTOOLS_ENABLED || !response?.data?.sessions) return response;
  const passes = state.localPasses[courseId] || [];
  if (!passes.length) return response;

  const sessions = response.data.sessions.map((s) =>
    passes.includes(passKey(s.sectionId, s.chapterId))
      ? { ...s, passed: true, attempted: true }
      : s,
  );
  return {
    ...response,
    data: { ...response.data, sessions, eligible: sessions.every((s) => s.passed) },
  };
}

export function patchProfile(response) {
  if (!DEVTOOLS_ENABLED || !state.levelOverride || !response?.data?.student) {
    return response;
  }
  // Only the logged-in student's own profile, not students a teacher looks up.
  let me = null;
  try {
    me = JSON.parse(localStorage.getItem("user"))?.userName;
  } catch {
    me = null;
  }
  const them = response.data.student.userName;
  if (!me || !them || String(me).toLowerCase() !== String(them).toLowerCase()) {
    return response;
  }
  return {
    ...response,
    data: {
      ...response.data,
      student: { ...response.data.student, gradeLevel: state.levelOverride },
    },
  };
}
